// ImageCaptionNode — TipTap node for excerptus.imageCaption.
// Stores `mediaId` (workspace_media.id) as the source of truth; the React
// node-view re-signs a fresh URL on mount. Legacy `src` is kept as a
// fallback for in-flight docs that haven't been re-saved yet.
import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer, NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import { ImageIcon, Upload, Loader2, Pencil, X, GripVertical } from "lucide-react";
import { useEffect, useRef, useState, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  uploadWorkspaceMedia,
  WorkspaceMediaCapError,
  WORKSPACE_MEDIA_ALLOWED_TYPES,
  WORKSPACE_MEDIA_MAX_BYTES,
  resolveSignedUrlByMediaId,
} from "@/lib/workspaceMedia";
import { WorkspaceMediaPanel } from "../WorkspaceMediaPicker";
import { supabase } from "@/integrations/supabase/client";
import { useBriefEditorContext } from "../briefEditorContext";
import { useToast } from "@/hooks/use-toast";
import { notifyHostDropHandled } from "@/lib/composerHostDropReset";

const ImageCaptionView = ({ node, updateAttributes, selected }: NodeViewProps) => {
  const { workspaceId, workspaceMediaCapBytes } = useBriefEditorContext();
  const { toast } = useToast();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const mediaId = (node.attrs.mediaId as string | null) ?? null;
  const legacySrc = (node.attrs.src as string) ?? "";
  const alt = (node.attrs.alt as string) ?? "";
  const caption = (node.attrs.caption as string) ?? "";
  const credit = (node.attrs.credit as string) ?? "";
  const size = ((node.attrs.size as string) ?? "md") as "sm" | "md" | "lg" | "full";
  const sizeMaxW: Record<typeof size, string> = { sm: "20rem", md: "28rem", lg: "42rem", full: "100%" };
  const sizeMaxH: Record<typeof size, string> = { sm: "20rem", md: "28rem", lg: "36rem", full: "none" };

  // Runtime-only signed URL resolved from mediaId. Falls back to legacy `src`.
  const [resolvedSrc, setResolvedSrc] = useState<string>(legacySrc);
  useEffect(() => {
    let cancelled = false;
    if (mediaId) {
      void (async () => {
        const url = await resolveSignedUrlByMediaId(mediaId);
        if (!cancelled && url) setResolvedSrc(url);
      })();
    } else {
      setResolvedSrc(legacySrc);
    }
    return () => { cancelled = true; };
  }, [mediaId, legacySrc]);

  const src = resolvedSrc;
  const altMissing = !alt.trim();

  const uploadFile = useCallback(async (file: File) => {
    if (!workspaceId) {
      toast({ title: "Save the brief first before uploading images.", variant: "destructive" });
      return;
    }
    if (!WORKSPACE_MEDIA_ALLOWED_TYPES.has(file.type)) {
      toast({ title: "Only PNG or JPEG images can be embedded.", variant: "destructive" });
      return;
    }
    setUploading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not signed in");
      const { row, signedUrl, deduplicated } = await uploadWorkspaceMedia({
        workspaceId,
        userId: user.id,
        file,
        capBytes: workspaceMediaCapBytes ?? null,
      });
      updateAttributes({ mediaId: row.id, src: null });
      setResolvedSrc(signedUrl);
      if (deduplicated) {
        toast({ title: "Reused existing image", description: "This image was already in your Workspace Media." });
      }
    } catch (err) {
      if (err instanceof WorkspaceMediaCapError) {
        toast({ title: "Storage cap reached", description: err.message, variant: "destructive" });
      } else {
        toast({
          title: "Upload failed",
          description: err instanceof Error ? err.message : "Try a smaller PNG or JPEG.",
          variant: "destructive",
        });
      }
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }, [workspaceId, workspaceMediaCapBytes, updateAttributes, toast]);

  const handlePick = () => inputRef.current?.click();
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void uploadFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (Array.from(e.dataTransfer?.types ?? []).includes("Files")) {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = "copy";
      setDragOver(true);
    }
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    const files = Array.from(e.dataTransfer?.files ?? []).filter((f) => f.type.startsWith("image/"));
    if (files.length === 0) return;
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    void uploadFile(files[0]);
    notifyHostDropHandled();
  };

  // ---- Filled state: clean inline figure ----
  if (src) {
    return (
      <NodeViewWrapper
        as="figure"
        className={`my-6 flex flex-col items-center ${selected ? "rounded-md ring-2 ring-primary/50" : ""}`}
      >
        <img
          src={src}
          alt={alt}
          style={{ maxWidth: sizeMaxW[size], maxHeight: sizeMaxH[size] }}
          className="w-full rounded object-contain"
        />
        {(caption.trim() || credit.trim()) && (
          <figcaption className="mt-2 text-center text-sm text-muted-foreground">
            {caption.trim() && <span>{caption}</span>}
            {credit.trim() && (
              <span className="ml-1 italic">{caption.trim() ? "— " : ""}{credit}</span>
            )}
          </figcaption>
        )}
        {altMissing && (
          <div className="mt-1 text-xs text-destructive">Alt text is required for accessibility.</div>
        )}

        {/* Hover/selected action bar */}
        <div className="mt-2 flex flex-wrap items-center justify-center gap-1 text-xs">
          <span
            data-drag-handle
            draggable
            contentEditable={false}
            title="Drag to move this image"
            className="inline-flex h-7 cursor-grab select-none items-center rounded px-1 text-muted-foreground hover:bg-muted active:cursor-grabbing"
            aria-label="Drag image"
          >
            <GripVertical className="h-3.5 w-3.5" />
          </span>
          <div
            role="group"
            aria-label="Image size"
            className="inline-flex h-7 items-center overflow-hidden rounded border bg-background"
          >
            {(["sm", "md", "lg", "full"] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => updateAttributes({ size: s })}
                aria-pressed={size === s}
                title={`Size: ${s === "full" ? "Full width" : s.toUpperCase()}`}
                className={`h-full px-2 text-[11px] font-medium transition-colors ${
                  size === s
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-muted"
                }`}
              >
                {s === "full" ? "Full" : s.toUpperCase()}
              </button>
            ))}
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={() => setEditing((v) => !v)} className="h-7 text-xs">
            <Pencil className="mr-1 h-3 w-3" />
            {editing ? "Hide details" : "Edit details"}
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={handlePick} className="h-7 text-xs" disabled={uploading}>
            {uploading ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Upload className="mr-1 h-3 w-3" />}
            Replace
          </Button>
          {workspaceId && (
            <Button type="button" variant="ghost" size="sm" onClick={() => setPickerOpen(true)} className="h-7 text-xs" disabled={uploading}>
              <ImageIcon className="mr-1 h-3 w-3" /> From Workspace Media
            </Button>
          )}
        </div>

        {editing && (
          <div className="mt-3 grid w-full max-w-xl gap-2 rounded-md border bg-muted/30 p-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label className="text-xs">
                Alt text <span className="text-destructive">*</span>
              </Label>
              <Input
                value={alt}
                onChange={(e) => updateAttributes({ alt: e.target.value })}
                placeholder="Describe the image for screen readers"
                className={`h-8 text-xs ${altMissing ? "border-destructive" : ""}`}
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Caption</Label>
              <Input
                value={caption}
                onChange={(e) => updateAttributes({ caption: e.target.value })}
                placeholder="Visible caption shown below the image"
                className="h-8 text-xs"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Credit</Label>
              <Input
                value={credit}
                onChange={(e) => updateAttributes({ credit: e.target.value })}
                placeholder="e.g. Photo: J. Doe"
                className="h-8 text-xs"
              />
            </div>
            <div className="sm:col-span-2 flex justify-end">
              <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(false)} className="h-7 text-xs">
                <X className="mr-1 h-3 w-3" /> Done
              </Button>
            </div>
          </div>
        )}

        <input
          ref={inputRef}
          type="file"
          accept={Array.from(WORKSPACE_MEDIA_ALLOWED_TYPES).join(",")}
          className="hidden"
          onChange={handleChange}
        />
        <input type="hidden" value={`${WORKSPACE_MEDIA_MAX_BYTES}`} />
        {workspaceId && (
          <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
            <DialogContent className="max-w-3xl">
              <DialogHeader>
                <DialogTitle>Pick from Workspace Media</DialogTitle>
              </DialogHeader>
              <WorkspaceMediaPanel
                workspaceId={workspaceId}
                capBytes={workspaceMediaCapBytes ?? null}
                onPick={(row, signedUrl) => {
                  updateAttributes({ mediaId: row.id, src: null });
                  setResolvedSrc(signedUrl);
                  setPickerOpen(false);
                }}
              />
            </DialogContent>
          </Dialog>
        )}
      </NodeViewWrapper>
    );
  }

  // ---- Empty state: picker card with drag-and-drop ----
  return (
    <NodeViewWrapper as="figure" className="my-6 rounded-lg border bg-card p-4">
      <div className="mb-3 flex items-center gap-2 text-xs text-muted-foreground">
        <ImageIcon className="h-4 w-4" aria-hidden />
        <span>Image with caption</span>
      </div>
      <div
        onDragOver={handleDragOver}
        onDragEnter={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`mb-3 grid gap-2 rounded-md p-1 transition-colors sm:grid-cols-2 ${
          dragOver ? "bg-primary/10 ring-2 ring-primary/60" : ""
        }`}
      >
        <Button
          type="button"
          variant="outline"
          onClick={handlePick}
          disabled={uploading}
          className="flex h-32 w-full flex-col items-center justify-center gap-1 border-dashed text-center"
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          <span className="text-sm">{uploading ? "Uploading…" : "Upload or drop PNG / JPEG"}</span>
          <span className="text-xs text-muted-foreground">max 2 MB</span>
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => setPickerOpen(true)}
          disabled={uploading || !workspaceId}
          className="flex h-32 w-full items-center justify-center gap-2 border-dashed"
        >
          <ImageIcon className="h-4 w-4" />
          From Workspace Media
        </Button>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={Array.from(WORKSPACE_MEDIA_ALLOWED_TYPES).join(",")}
        className="hidden"
        onChange={handleChange}
      />
      <div className="grid gap-2 sm:grid-cols-2">
        <div>
          <Label className="text-xs">
            Alt text <span className="text-destructive">*</span>
          </Label>
          <Input
            value={alt}
            onChange={(e) => updateAttributes({ alt: e.target.value })}
            placeholder="Describe the image for screen readers"
            className={`h-8 text-xs ${altMissing ? "border-destructive" : ""}`}
          />
        </div>
        <div>
          <Label className="text-xs">Credit (optional)</Label>
          <Input
            value={credit}
            onChange={(e) => updateAttributes({ credit: e.target.value })}
            placeholder="e.g. Photo: J. Doe"
            className="h-8 text-xs"
          />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs">Caption (optional)</Label>
          <Input
            value={caption}
            onChange={(e) => updateAttributes({ caption: e.target.value })}
            placeholder="Visible caption shown below the image"
            className="h-8 text-xs"
          />
        </div>
      </div>
      <input type="hidden" value={`${WORKSPACE_MEDIA_MAX_BYTES}`} />
      {workspaceId && (
        <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Pick from Workspace Media</DialogTitle>
            </DialogHeader>
            <WorkspaceMediaPanel
              workspaceId={workspaceId}
              capBytes={workspaceMediaCapBytes ?? null}
              onPick={(row, signedUrl) => {
                updateAttributes({ mediaId: row.id, src: null });
                setResolvedSrc(signedUrl);
                setPickerOpen(false);
              }}
            />
          </DialogContent>
        </Dialog>
      )}
    </NodeViewWrapper>
  );
};

export const LegacyImageCaptionNode = Node.create({
  name: "excerptus.imageCaption",
  group: "block",
  atom: true,
  selectable: true,
  draggable: true,
  addAttributes() {
    return {
      // Source of truth — references workspace_media.id. Survives URL expiry.
      mediaId: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-media-id") || null,
        renderHTML: (attrs) => (attrs.mediaId ? { "data-media-id": attrs.mediaId as string } : {}),
      },
      // Legacy / runtime-resolved signed URL. Never serialized to HTML.
      src: {
        default: "",
        parseHTML: () => "",
        renderHTML: () => ({}),
      },
      alt: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-alt") || "",
        renderHTML: (attrs) => (attrs.alt ? { "data-alt": attrs.alt as string } : {}),
      },
      caption: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-caption") || null,
        renderHTML: (attrs) => (attrs.caption ? { "data-caption": attrs.caption as string } : {}),
      },
      credit: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-credit") || null,
        renderHTML: (attrs) => (attrs.credit ? { "data-credit": attrs.credit as string } : {}),
      },
      width: { default: null },
      height: { default: null },
      size: {
        default: "md",
        parseHTML: (el) => (el.getAttribute("data-size") as "sm" | "md" | "lg" | "full" | null) || "md",
        renderHTML: (attrs) => (attrs.size && attrs.size !== "md" ? { "data-size": attrs.size as string } : {}),
      },
    };
  },
  parseHTML() {
    return [{ tag: "figure.image-caption" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["figure", mergeAttributes(HTMLAttributes, { class: "image-caption" })];
  },
  addNodeView() {
    return ReactNodeViewRenderer(ImageCaptionView);
  },
});
